import { Circle } from "@phosphor-icons/react"
import {
  Button,
  Modal,
  NoDataFound,
  Spinner,
  Toast,
} from "@vesatogo/grass-core"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { useState } from "react"
import toast from "react-hot-toast"
import {
  ReceiptAdvancePaymentsQuery,
  useReceiptAdvancePaymentsQuery,
  useSendAdvanceRequestMutation,
} from "~/generated/graphql"

type Advance = ReceiptAdvancePaymentsQuery["advance_payment"][0]

const GRID_CLASS =
  "grid grid-cols-[85px_110px_110px_130px_140px_160px] gap-3 items-center"

export const SendAdvanceRequestModal = ({
  isOpen = false,
  onClose,
  receiptId,
}: {
  isOpen?: boolean
  onClose?: any
  receiptId?: string | number
}) => {
  const [sendingId, setSendingId] = useState<Advance["id"] | null>(null)
  const [{ data, fetching }] = useReceiptAdvancePaymentsQuery({
    variables: { receiptId },
    pause: !isOpen || !receiptId,
    requestPolicy: "network-only",
  })
  const [, requestAdvancePayment] = useSendAdvanceRequestMutation()

  const advances = data?.advance_payment || []

  const sendWhatsApp = async (id: Advance["id"]) => {
    setSendingId(id)
    const { error } = await requestAdvancePayment({
      advance_payment_ids: [id],
    })
    setSendingId(null)
    if (error) {
      return toast.custom(<Toast title={error.message} intent="danger" />)
    }
    return toast.custom(
      <Toast title={`WhatsApp message sent for Advance #${id}`} />
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Send Advance Request - Receipt #${receiptId}`}
      bodyClassName="max-w-3xl"
      secondaryActionButtonProps={{ text: "Close", onClick: onClose }}
    >
      <div className="text-sm">
        <div
          className={classNames(
            GRID_CLASS,
            "text-gray-700 font-500 border-b border-gray-200 pb-2"
          )}
        >
          <span>Advance Id</span>
          <span>Created On</span>
          <span>Type</span>
          <span>Requested Amount</span>
          <span>Advance Status</span>
          <span> </span>
        </div>
        {fetching ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : advances.length === 0 ? (
          <NoDataFound title="No advances found for this receipt" />
        ) : (
          advances.map(advance => {
            const status = advance?.status
            return (
              <div
                key={advance.id}
                className={classNames(
                  GRID_CLASS,
                  "border-b border-gray-100 py-3"
                )}
              >
                <span className="link-text w-fit">#{advance.id}</span>
                <span>
                  {dayjs(advance.created_at).format("D MMM 'YY")}
                  <br />
                  <span className="text-xs text-gray-500">
                    {dayjs(advance.created_at).format("hh:mm a")}
                  </span>
                </span>
                <span>{advance?.parameter?.name || "--"}</span>
                <span className="font-700">{inr(advance.amount)}</span>
                <span
                  className={classNames(
                    "flex gap-1 items-center font-600",
                    status === "Draft"
                      ? "text-yellow"
                      : status === "Requested"
                      ? "text-blue"
                      : "text-green"
                  )}
                >
                  <Circle weight="fill" />
                  {status}
                </span>
                <Button
                  intent="primary"
                  size="sm"
                  hidden={advance?.status !== "Draft"}
                  className="!w-fit"
                  loading={sendingId === advance.id}
                  disabled={sendingId !== null && sendingId !== advance.id}
                  onClick={() => {
                    sendWhatsApp(advance.id)
                    onClose?.()
                  }}
                >
                  Send
                </Button>
              </div>
            )
          })
        )}
      </div>
    </Modal>
  )
}

export default SendAdvanceRequestModal
